import * as React from "react"
import {MouseEvent} from "react"
import {Popup, Search, SearchProps, SearchResultProps} from "semantic-ui-react"
import {Place} from "../model/places/PlaceTypes"
import {PlaceSuggestion} from "./PlaceSuggestion"
import {Locale} from "../model/BookingTypes"
import {ID} from "../model/id"
import {Loading, loadingIfNull, ready} from "../tsutil"
import {debounce} from "lodash"
import {PlaceSuggest as PLACE_SUGGESTIONS} from "../graphql/queries"
import {GraphQL} from "../graphql"

type PlaceSuggestions = Array<Place>

interface OwnProps {
  onSelect: (suggestion: Place) => void
  placeholder: string
  locale: Locale
  disabled: boolean
  showPopupHelp?: boolean
  raw?: boolean
}

type Props = OwnProps & SuggestionProps & StateProps

interface Result {
  id: number
  key: ID
}

class PlaceSuggest extends React.Component<Props, {}> {
  // noinspection JSMethodCanBeStatic
  results(placeSuggestions: Array<Place>) {
    // This results shape can't have extraneous props, that's
    // why we need to keep the original placeSuggestions too...
    return placeSuggestions.map((suggestion, index) => ({
      id: index,
      key: suggestion.id,
      title: suggestion.id,
    }))
  }

  handleResultSelect(placeSuggestions: Array<Place>) {
    return async (e: React.MouseEvent<HTMLDivElement>, data: SearchProps) => {
      // The Semantic UI Search component will update itself after calling this handler, but
      // calling this.props.onSelect() causes this component to be dismounted, which causes the
      // Search component to emit "Warning: Can only update a mounted or mounting component".
      // So call this.props.onSelect() asynchronously instead by awaiting for null first (this
      // causes the function to immediately return and the rest of it to finish on a different
      // stack).
      await null
      const result: Result = data.result
      this.props.onSelect(placeSuggestions[result.id])
    }
  }

  handleSearchChange = (e: MouseEvent<HTMLElement>, data: SearchProps) => {
    this.props.onInput(data.value || "")
  }

  handleResultRender(placeSuggestions: Array<Place>) {
    return (props: SearchResultProps): any => {
      if (props.id !== undefined) {
        const id = Number.parseInt(props.id.toString(), 10)
        return <PlaceSuggestion place={ready(placeSuggestions[id])} />
      }
      return null
    }
  }

  render() {
    const {loading, placeholder, placeSuggestions, searchText, showPopupHelp, disabled} = this.props

    // We *must* render the Search component, even while loading, as otherwise it drops off the UI.
    // But the placeSuggestions.value array retains its previous value even while a new query is
    // in flight so there is no flash of a zero results list.
    const suggestions = placeSuggestions.getOrElse([])

    const SearchComponent = (
      <Search
        disabled={disabled}
        placeholder={placeholder}
        loading={loading}
        onResultSelect={this.handleResultSelect(suggestions)}
        onSearchChange={this.handleSearchChange}
        results={this.results(suggestions)}
        value={searchText}
        resultRenderer={this.handleResultRender(suggestions)}
        showNoResults={false}
        minCharacters={0}
        fluid
      />
    )

    return showPopupHelp ? (
      <Popup
        trigger={SearchComponent}
        content="For Unit, Level etc. use a slash, eg. 1/40 Kemp Av"
        position="top center"
        on="click"
      />
    ) : (
      SearchComponent
    )
  }
}

interface SuggestionProps {
  loading: boolean
  placeSuggestions: Loading<PlaceSuggestions>
}

function WithSuggestions(props: OwnProps & StateProps) {
  const {locale, serverSearchText, raw} = props
  return (
    <GraphQL.Query
      query={PLACE_SUGGESTIONS}
      variables={{
        localeId: locale.id,
        text: serverSearchText,
        raw,
      }}
      render={({data, loading}) => {
        return (
          <PlaceSuggest
            {...props}
            loading={loading}
            placeSuggestions={loadingIfNull(data).map((data) => data.placeSuggestions)}
          />
        )
      }}
    />
  )
}

interface StateProps {
  searchText: string
  /**
   * The search text ready to send to the server. May be different from searchText due to
   * debouncing.
   */
  serverSearchText: string
  onInput: (text: string) => void
}

interface State {
  searchText: string
  serverSearchText: string
}

class PlaceSuggestState extends React.Component<OwnProps, State> {
  constructor(props: OwnProps) {
    super(props)
    this.state = PlaceSuggestState.initialState
  }

  static initialState = {
    searchText: "",
    serverSearchText: "",
  }

  setServerSearchText = debounce((text: string) => {
    this.setState({serverSearchText: text})
  }, 250)

  handleInput = (text: string) => {
    this.setState({searchText: text})
    this.setServerSearchText(text)
  }

  render(): React.ReactNode {
    return (
      <WithSuggestions
        {...this.props}
        onSelect={(x) => {
          this.setState(PlaceSuggestState.initialState)
          this.props.onSelect(x)
        }}
        {...this.state}
        onInput={this.handleInput}
      />
    )
  }
}

export {PlaceSuggestState as PlaceSuggest}



// WEBPACK FOOTER //
// ./src/components/PlaceSuggest.tsx